/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { createElement, useContext, useMemo } from "react"

import classNames from "classnames"

import {
  LinkListBehavior,
  LinkListContext
} from "shared/context/LinkListContext"
import { TabType } from "shared/types/Tab"
import { Accordion } from "shared/ui/accordion/Accordion"
import { Link, LinkProps } from "shared/ui/link/Link"
import { Readonly } from "shared/ui/readonly/Readonly"

import "./styles/_link-list.scss"

export enum LinkListVariant {
  FOOTER = "footer-link-list",
  SIDEBAR = "sidebar-link-list"
}

interface LinkListProps {
  label?: React.ReactNode
  "aria-label"?: string | null
  className?: string
  variant?: LinkListVariant
  children?: React.ReactNode | React.ReactNode[]
  tag?: "div" | "nav" | "ul"
}

const LinkListLink = React.memo(
  ({
    wrapperProps,
    ...props
  }: LinkProps & {
    wrapperProps?: React.PropsWithoutRef<React.HTMLProps<HTMLLIElement>>
  }) => {
    return (
      <LinkListLinkWrapper {...wrapperProps}>
        <Link {...props} />
      </LinkListLinkWrapper>
    )
  }
)

const LinkListLinkWrapper = React.memo(
  React.forwardRef(
    (
      {
        children,
        ...props
      }: React.PropsWithoutRef<React.HTMLProps<HTMLLIElement>>,
      ref: React.ForwardedRef<HTMLLIElement>
    ) => {
      const { onClick } = useContext(LinkListContext)

      return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <li {...props} className="link-list__link" onClick={onClick} ref={ref}>
          {children}
        </li>
      )
    }
  )
)

const LinkListGenericWrapper = React.memo(
  React.forwardRef(
    (
      {
        children,
        ...props
      }: React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>>,
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { onClick } = useContext(LinkListContext)

      return (
        <div {...props} className="link-list__link" onClick={onClick} ref={ref}>
          {children}
        </div>
      )
    }
  )
)

const LinkListContainer = React.memo((props: LinkListProps) => {
  const { behavior } = useContext(LinkListContext)

  const {
    children,
    label,
    className,
    variant = LinkListVariant.FOOTER,
    tag = "ul"
  } = props

  const tabs: TabType[] = useMemo(
    () => [
      {
        title: label,
        content: <div className="link-list__list">{children}</div>,
        key: "default"
      }
    ],
    [label, children]
  )

  if (behavior === LinkListBehavior.ACCORDION) {
    return (
      <Accordion className={classNames("link-list", className)} tabs={tabs} />
    )
  }

  return (
    <Readonly.Root className={classNames("link-list", variant, className)}>
      <Readonly.Label className="link-list__label">{label}</Readonly.Label>
      <Readonly.Value>
        {createElement(tag, { className: "link-list__list" }, children)}
      </Readonly.Value>
    </Readonly.Root>
  )
})

type CompoundLinkListType = typeof LinkListContainer & {
  Link: typeof LinkListLink
  LinkWrapper: typeof LinkListLinkWrapper
  GenericWrapper: typeof LinkListGenericWrapper
}

const LinkList: CompoundLinkListType = LinkListContainer as CompoundLinkListType

LinkList.Link = LinkListLink
LinkList.LinkWrapper = LinkListLinkWrapper
LinkList.GenericWrapper = LinkListGenericWrapper

export { LinkList }
